import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
} from 'reactstrap';
import Breadcrumbs from '../../components/Common/Breadcrumb';
import LoadingSpiner from '../components/LoadingSpiner';
import {
  capitalizeWords,
  formatPhoneNumber,
  formatPrice,
} from '../components/capitalizeFunction';
import React, { useEffect, useMemo, useState } from 'react';
import Swal from 'sweetalert2';
import {
  useDeleteCommande,
  usePagignationCommandes,
} from '../../Api/queriesCommande';
import { useNavigate } from 'react-router-dom';
import { connectedUserBoutique } from '../Authentication/userInfos';
import ListPagination from '../components/ListPagination';

export default function CommandeListe() {
  /**
   * -----------------------------
   * MODIF (performance + RAM)
   * -----------------------------
   * Avant: `useAllCommandes()` chargeait toutes les commandes + factures,
   * puis on filtrait côté frontend => très lourd quand la base grossit.
   *
   * Maintenant: on utilise la pagination + filtres côté serveur via `usePagignationCommandes`
   * (sans changer le nom du hook ni l'API). Résultat: requêtes plus rapides et RAM stable.
   */
  const [page, setPage] = useState(1);
  const limit = 30;
  const { mutate: deleteCommandeAndRestorStock } = useDeleteCommande();

  // State de chargement pour la suppression
  const [isDeleting, setIsDeletting] = useState(false);

  // Annuler une Ordonnance
  const navigate = useNavigate();

  // Navigation ver la FACTURE avec ID de Paiement
  const handleCommandeClick = (id) => {
    navigate(`/facture/${id}`);
  };

  // ---------------------------
  // Fonction pour exeuter l'annulation de la décrementation des stocks
  function deleteCommande(comm) {
    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-success ms-2',
        cancelButton: 'btn btn-danger me-2',
      },
      buttonsStyling: false,
    });

    swalWithBootstrapButtons
      .fire({
        title: `Attention après l'Annulation les produits seront ajouter sur votre STOCK !  `,
        text: 'Voulez-vous continuer ?',
        icon: 'question',
        iconColor: 'red',
        showCancelButton: true,
        confirmButtonText: 'Oui, Continuer',
        cancelButtonText: 'Non',
        reverseButtons: true,
      })
      .then((result) => {
        if (result.isConfirmed) {
          try {
            const payload = {
              commandeId: comm?._id,
              items: comm?.items.map((item) => ({
                produit: item.produit,
                quantity: item.quantity,
              })),
            };

            // --------------------------------
            setIsDeletting(true);
            // Exécuter l'annulation
            deleteCommandeAndRestorStock(payload, {
              onSuccess: () => {
                setIsDeletting(false);
                swalWithBootstrapButtons.fire({
                  title: 'Succès!',
                  text: `Commande Annulé avec succès les produits sont ajouté sur le STOCK.`,
                  icon: 'success',
                });
              },
              onError: (e) => {
                setIsDeletting(false);
                swalWithBootstrapButtons.fire({
                  title: 'Erreur',
                  text:
                    e?.response?.data?.message ||
                    'Une erreur est survenue lors de la suppression.',
                  icon: 'error',
                });
              },
            });
          } catch (e) {
            setIsDeletting(false);
            swalWithBootstrapButtons.fire({
              title: 'Erreur',
              text:
                e ||
                e?.response?.data?.message ||
                "Une erreur est survenue lors de l'Annulation.",
              icon: 'error',
            });
          }
        } else if (result.dismiss === Swal.DismissReason.cancel) {
          setIsDeletting(false);
          swalWithBootstrapButtons.fire({
            title: 'Commande non Annulée',
            icon: 'error',
          });
        }
      });
  }
  // ------------------------------------------------------------
  const [searchTerm, setSearchTerm] = useState('');
  const [todayCommande, setTodayCommande] = useState(false);
  const [delivredCommande, setDelivredCommande] = useState(false);
  const [notDelivredCommande, setNotdelivredCommande] = useState(false);
  const [selectedBoutique, setSelectedBoutique] = useState(null);
  /**
   * MODIF:
   * - debounce sur searchTerm
   * - on transmet les filtres au backend (search/boutique/today/statut)
   * - `filterCommandes` devient les données déjà filtrées côté serveur
   */
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const statut = delivredCommande
    ? 'en cours'
    : notDelivredCommande
    ? 'en attente'
    : null;

  useEffect(() => {
    // UX: changement filtre => retour page 1
    setPage(1);
  }, [debouncedSearchTerm, selectedBoutique, todayCommande, statut]);

  const {
    data: commandes,
    isLoading,
    error,
  } = usePagignationCommandes(
    page,
    limit,
    debouncedSearchTerm,
    selectedBoutique,
    statut,
    todayCommande
  );

  const filterCommandes = useMemo(
    () => commandes?.commandes?.data || [],
    [commandes]
  );

  const facturesByCommandeId = useMemo(() => {
    const map = new Map();
    (commandes?.factures?.data || []).forEach((fact) => {
      const id = fact?.commande?._id;
      if (id != null) map.set(String(id), fact);
    });
    return map;
  }, [commandes?.factures?.data]);

  // Même logique que FactureListe / SelectedFacture (total + reliquat)
  const getMontantsCommande = (comm) => {
    const facture = facturesByCommandeId.get(String(comm?._id));
    const totalAmount = Number(comm?.totalAmount) || 0;
    const sheepingFee =
      Number(comm?.sheepingFee) > 0 ? Number(comm.sheepingFee) : 0;
    const montantCommande =
      sheepingFee > 0 ? totalAmount + sheepingFee : totalAmount;

    const montantRestant = facture
      ? (Number(facture.totalAmount) || 0) - (Number(facture.totalPaye) || 0)
      : montantCommande;

    return { montantCommande, montantRestant };
  };

  // Total Commandes Livrés
  const totalCommandesLivres = commandes?.commandes?.counts?.livre ?? 0;

  // Commande en Attente
  const commandesEnAttente = commandes?.commandes?.counts?.enAttente ?? 0;

  // Commande en Cours
  const commandesEnCours = commandes?.commandes?.counts?.enCours ?? 0;

  const hasActiveFilters =
    Boolean(searchTerm) ||
    todayCommande ||
    delivredCommande ||
    notDelivredCommande ||
    selectedBoutique != null;

  const resetFilters = () => {
    setSearchTerm('');
    setTodayCommande(false);
    setDelivredCommande(false);
    setNotdelivredCommande(false);
    setSelectedBoutique(null);
  };

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <Breadcrumbs
            title='Commande'
            breadcrumbItem='Historique des Commandes'
          />
          {/* -------------------------- */}

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  <div id='commandeList'>
                    {/* Barre filtres + KPI */}
                    <div className='border rounded-3 p-3 mb-4 bg-light'>
                      <Row className='g-3 align-items-end'>
                        <Col xl={4} lg={5} md={6}>
                          <Label
                            htmlFor='filterBoutique'
                            className='fw-semibold mb-1'
                          >
                            Boutique
                          </Label>
                          <div className='d-flex gap-2 flex-wrap'>
                            <Input
                              id='filterBoutique'
                              type='select'
                              bsSize='sm'
                              value={selectedBoutique ?? ''}
                              onChange={(e) => {
                                const v = e.target.value;
                                setSelectedBoutique(
                                  v === '' ? null : Number(v)
                                );
                              }}
                              className='border border-dark flex-grow-1'
                              style={{ cursor: 'pointer', minWidth: '160px' }}
                            >
                              <option value=''>Toutes les boutiques</option>
                              <option value={connectedUserBoutique ?? 0}>
                                {connectedUserBoutique ?? 0} — Ma boutique
                              </option>
                              <optgroup label='Raccourcis'>
                                <option value='1'>Boutique 1</option>
                                <option value='2'>Boutique 2</option>
                              </optgroup>
                            </Input>
                            <Input
                              type='number'
                              bsSize='sm'
                              className='border border-dark'
                              placeholder='N°'
                              title='Numéro de boutique'
                              style={{ maxWidth: '90px' }}
                              value={selectedBoutique ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value;
                                setSelectedBoutique(
                                  raw === '' ? null : Number(raw)
                                );
                              }}
                            />
                          </div>
                        </Col>

                        <Col xl={5} lg={5} md={6}>
                          <Label
                            htmlFor='filterSearch'
                            className='fw-semibold mb-1'
                          >
                            Recherche
                          </Label>
                          <div className='d-flex gap-2'>
                            <Input
                              id='filterSearch'
                              type='text'
                              bsSize='sm'
                              className='border border-dark'
                              placeholder='Client, adresse, téléphone, statut…'
                              value={searchTerm}
                              onChange={(e) => setSearchTerm(e.target.value)}
                            />
                            {searchTerm !== '' && (
                              <Button
                                color='danger'
                                size='sm'
                                outline
                                title='Effacer la recherche'
                                onClick={() => setSearchTerm('')}
                              >
                                <i className='fas fa-times'></i>
                              </Button>
                            )}
                          </div>
                        </Col>

                        <Col xl={3} lg={2} className='text-md-end'>
                          {hasActiveFilters && (
                            <Button
                              color='secondary'
                              size='sm'
                              outline
                              className='w-100 w-md-auto'
                              onClick={resetFilters}
                            >
                              <i className='ri-filter-off-line me-1'></i>
                              Réinitialiser
                            </Button>
                          )}
                        </Col>
                      </Row>

                      <Row className='g-3 mt-1 align-items-stretch'>
                        <Col lg={6}>
                          <Label className='fw-semibold mb-2 d-block'>
                            Filtres rapides
                          </Label>
                          <div className='d-flex flex-wrap gap-2'>
                            <Button
                              color={todayCommande ? 'warning' : 'light'}
                              size='sm'
                              className={
                                todayCommande
                                  ? 'text-white'
                                  : 'border border-secondary text-dark'
                              }
                              onClick={() => setTodayCommande(!todayCommande)}
                            >
                              <i className='ri-calendar-line me-1'></i>
                              Aujourd&apos;hui
                            </Button>
                            <Button
                              color={delivredCommande ? 'info' : 'light'}
                              size='sm'
                              className={
                                delivredCommande
                                  ? 'text-white'
                                  : 'border border-secondary text-dark'
                              }
                              onClick={() =>
                                setDelivredCommande(!delivredCommande)
                              }
                            >
                              <i className='ri-time-line me-1'></i>
                              En cours
                            </Button>
                            <Button
                              color={notDelivredCommande ? 'danger' : 'light'}
                              size='sm'
                              className={
                                notDelivredCommande
                                  ? 'text-white'
                                  : 'border border-secondary text-dark'
                              }
                              onClick={() =>
                                setNotdelivredCommande(!notDelivredCommande)
                              }
                            >
                              <i className='ri-hourglass-line me-1'></i>
                              En attente
                            </Button>
                          </div>
                        </Col>

                        {!isLoading && !error && (
                          <Col lg={6}>
                            <Label className='fw-semibold mb-2 d-block'>
                              Synthèse
                            </Label>
                            <Row className='g-2'>
                              <Col xs={4}>
                                <div className='border rounded bg-white p-2 text-center h-100'>
                                  <div className='text-muted small'>
                                    Livrées
                                  </div>
                                  <div className='text-info fw-bold'>
                                    {formatPrice(totalCommandesLivres) || 0}
                                  </div>
                                </div>
                              </Col>
                              <Col xs={4}>
                                <div className='border rounded bg-white p-2 text-center h-100'>
                                  <div className='text-muted small'>
                                    En cours
                                  </div>
                                  <div className='text-primary fw-bold'>
                                    {formatPrice(commandesEnCours) || 0}
                                  </div>
                                </div>
                              </Col>
                              <Col xs={4}>
                                <div className='border rounded bg-white p-2 text-center h-100'>
                                  <div className='text-muted small'>
                                    En attente
                                  </div>
                                  <div className='text-danger fw-bold'>
                                    {formatPrice(commandesEnAttente) || 0}
                                  </div>
                                </div>
                              </Col>
                            </Row>
                          </Col>
                        )}
                      </Row>
                    </div>

                    {!error && !isLoading && (
                      <ListPagination
                        page={commandes?.commandes?.page ?? page}
                        totalPages={commandes?.commandes?.totalPages ?? 1}
                        onPageChange={setPage}
                        className='mb-3'
                      />
                    )}

                    {error && (
                      <div className='text-danger text-center'>
                        Erreur de chargement des données
                      </div>
                    )}
                    {isLoading && <LoadingSpiner />}

                    <div className='table-responsive table-card mt-3 mb-1'>
                      {filterCommandes?.length === 0 && (
                        <div className='text-center text-mutate'>
                          Aucune commande pour le moment !
                        </div>
                      )}
                      {!error && !isLoading && filterCommandes?.length > 0 && (
                        <table
                          className='table align-middle table-nowrap table-hover'
                          id='commandeTable'
                        >
                          <thead className='table-light'>
                            <tr className='text-center align-middle'>
                              <th
                                scope='col'
                                style={{ width: '48px' }}
                                title='Paiement'
                              >
                                <i
                                  className='fas fa-dollar-sign text-warning'
                                  aria-hidden='true'
                                ></i>
                              </th>
                              <th scope='col' className='text-start'>
                                Vendeur
                                <div className='small fw-normal text-muted'>
                                  Boutique
                                </div>
                              </th>
                              <th scope='col' style={{ minWidth: '110px' }}>
                                Date
                              </th>
                              <th scope='col'>Client</th>
                              <th scope='col'>Contact</th>
                              <th scope='col' style={{ width: '80px' }}>
                                Articles
                              </th>
                              <th scope='col' style={{ minWidth: '120px' }}>
                                Montant
                                <div className='small fw-normal'>
                                  <span className='text-primary'>Total</span>
                                  {' / '}
                                  <span className='text-danger'>Reste</span>
                                </div>
                              </th>
                              <th scope='col'>Statut</th>
                              <th scope='col' style={{ width: '120px' }}>
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className='list form-check-all text-center'>
                            {filterCommandes?.length > 0 &&
                              filterCommandes?.map((comm) => {
                                const { montantCommande, montantRestant } =
                                  getMontantsCommande(comm);

                                return (
                                  <tr key={comm?._id} className='text-center'>
                                    <th scope='row'>
                                      {commandes?.factures?.data?.some(
                                        (fact) =>
                                          fact?.commande?._id === comm?._id
                                      ) ? (
                                        <i className='fas fa-check-circle text-success'></i>
                                      ) : (
                                        <i className='fas fa-times-circle text-danger'></i>
                                      )}{' '}
                                    </th>
                                    <td className='text-start'>
                                      <div>
                                        {comm?.user?.name
                                          ? capitalizeWords(comm.user.name)
                                          : '—'}
                                      </div>
                                      <small className='text-muted'>
                                        Boutique:{' '}
                                        {comm?.user?.boutique != null
                                          ? String(comm.user.boutique)
                                          : '—'}
                                      </small>
                                    </td>
                                    <th scope='row'>
                                      {new Date(
                                        comm?.commandeDate ?? comm?.createdAt
                                      ).toLocaleDateString('fr-Fr', {
                                        weekday: 'short',
                                        year: 'numeric',
                                        month: '2-digit',
                                        day: '2-digit',
                                      })}
                                    </th>

                                    <td>{capitalizeWords(comm?.fullName)}</td>
                                    <td>
                                      {capitalizeWords(comm?.adresse)}

                                      <p>
                                        {formatPhoneNumber(comm?.phoneNumber) ||
                                          '------'}
                                      </p>
                                    </td>

                                    <td>
                                      {comm?.items?.length} acticles
                                      {'  '}
                                    </td>
                                    <td>
                                      <div className='text-primary fw-semibold'>
                                        {formatPrice(montantCommande)} F
                                      </div>
                                      <div className='text-danger fw-semibold'>
                                        {formatPrice(montantRestant)} F
                                      </div>
                                    </td>
                                    <td>
                                      <span
                                        className={`badge badge-soft-${
                                          comm?.statut === 'livré'
                                            ? 'success'
                                            : 'warning'
                                        }
                                         text-uppercase`}
                                      >
                                        {comm?.statut}
                                      </span>
                                    </td>

                                    <td>
                                      {isDeleting && <LoadingSpiner />}
                                      {!isDeleting && (
                                        <div className='d-flex gap-2'>
                                          <div className='show-details'>
                                            <button
                                              className='btn btn-sm btn-info show-item-btn'
                                              data-bs-toggle='modal'
                                              data-bs-target='#showdetails'
                                              onClick={() => {
                                                handleCommandeClick(comm?._id);
                                              }}
                                            >
                                              <i className=' bx bx-show-alt text-white'></i>
                                            </button>
                                          </div>
                                          {Number(comm.user?.boutique) ===
                                            Number(connectedUserBoutique) && (
                                            <div className='d-flex gap-2'>
                                              <div className='edit'>
                                                <button
                                                  className='btn btn-sm btn-success edit-item-btn'
                                                  onClick={() => {
                                                    navigate(
                                                      `/updateCommande/${comm?._id}`
                                                    );
                                                  }}
                                                >
                                                  <i className='ri-pencil-fill text-white'></i>
                                                </button>
                                              </div>
                                              <div className='remove'>
                                                <button
                                                  className='btn btn-sm btn-danger remove-item-btn'
                                                  data-bs-toggle='modal'
                                                  data-bs-target='#deleteRecordModal'
                                                  onClick={() => {
                                                    deleteCommande(comm);
                                                  }}
                                                >
                                                  <i className='ri-delete-bin-fill text-white'></i>
                                                </button>
                                              </div>
                                            </div>
                                          )}
                                        </div>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}
